import React, { useEffect, useState } from 'react';
import {
  Plus,
  Pencil,
  Trash2,
  X,
  Save,
  RefreshCw,
  ArrowUp,
  ArrowDown,
  Tag,
  AlertTriangle,
} from 'lucide-react';
import adminPricingService, {
  PricingPlan,
  PricingPlanInput,
} from '../../../services/adminPricingService';
import './ManagePricing.css';

const EMPTY_FORM: PricingPlanInput = {
  name: '',
  tagline: '',
  price: '',
  period: '',
  badge: '',
  cta_text: 'Get Started',
  features: [],
  is_active: true,
};

const ManagePricing: React.FC = () => {
  const [plans, setPlans] = useState<PricingPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reordering, setReordering] = useState(false);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<PricingPlanInput>(EMPTY_FORM);
  const [featuresText, setFeaturesText] = useState('');
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState<PricingPlan | null>(null);
  const [deleting, setDeleting] = useState(false);

  const [toast, setToast] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const showToast = (type: 'success' | 'error', text: string) => {
    setToast({ type, text });
    window.setTimeout(() => setToast(null), 3500);
  };

  const loadPlans = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await adminPricingService.getAll();
      setPlans(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load pricing plans');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPlans();
  }, []);

  const openCreateModal = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setFeaturesText('');
    setFormErrors({});
    setIsModalOpen(true);
  };

  const openEditModal = (plan: PricingPlan) => {
    setEditingId(plan.id);
    setForm({
      name: plan.name,
      tagline: plan.tagline || '',
      price: plan.price,
      period: plan.period || '',
      badge: plan.badge || '',
      cta_text: plan.cta_text || 'Get Started',
      features: plan.features,
      is_active: !!plan.is_active,
    });
    setFeaturesText(plan.features.join('\n'));
    setFormErrors({});
    setIsModalOpen(true);
  };

  const closeModal = () => {
    if (saving) return;
    setIsModalOpen(false);
  };

  const validate = (): boolean => {
    const errors: Record<string, string> = {};
    if (!form.name.trim()) errors.name = 'Plan name is required';
    if (!form.price.trim()) errors.price = 'Price is required';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    const features = featuresText
      .split('\n')
      .map((f) => f.trim())
      .filter(Boolean);

    const payload: PricingPlanInput = {
      ...form,
      name: form.name.trim(),
      price: form.price.trim(),
      tagline: form.tagline?.trim() || null,
      period: form.period?.trim() || null,
      badge: form.badge?.trim() || null,
      cta_text: form.cta_text?.trim() || 'Get Started',
      features,
    };

    setSaving(true);
    try {
      if (editingId) {
        const updated = await adminPricingService.update(editingId, payload);
        setPlans((prev) => prev.map((p) => (p.id === editingId ? updated : p)));
        showToast('success', 'Pricing plan updated');
      } else {
        const created = await adminPricingService.create(payload);
        setPlans((prev) => [...prev, created]);
        showToast('success', 'Pricing plan created');
      }
      setIsModalOpen(false);
    } catch (err) {
      showToast('error', err instanceof Error ? err.message : 'Failed to save plan');
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await adminPricingService.remove(deleteTarget.id);
      setPlans((prev) => prev.filter((p) => p.id !== deleteTarget.id));
      showToast('success', `"${deleteTarget.name}" deleted`);
      setDeleteTarget(null);
    } catch (err) {
      showToast('error', err instanceof Error ? err.message : 'Failed to delete plan');
    } finally {
      setDeleting(false);
    }
  };

  const toggleActive = async (plan: PricingPlan) => {
    try {
      const updated = await adminPricingService.update(plan.id, { is_active: !plan.is_active });
      setPlans((prev) => prev.map((p) => (p.id === plan.id ? updated : p)));
    } catch (err) {
      showToast('error', err instanceof Error ? err.message : 'Failed to update status');
    }
  };

  const movePlan = async (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= plans.length) return;

    const reordered = [...plans];
    const [moved] = reordered.splice(index, 1);
    reordered.splice(target, 0, moved);
    setPlans(reordered);

    setReordering(true);
    try {
      const saved = await adminPricingService.reorder(reordered.map((p) => p.id));
      setPlans(saved);
    } catch (err) {
      showToast('error', err instanceof Error ? err.message : 'Failed to reorder plans');
      loadPlans();
    } finally {
      setReordering(false);
    }
  };

  const activeCount = plans.filter((p) => !!p.is_active).length;

  return (
    <div className="manage-pricing mp-page">
      {toast && (
        <div className={`mp-toast ${toast.type}`} role="status">
          {toast.text}
        </div>
      )}

      <div className="mp-header">
        <div>
          <h1 className="mp-title">
            <span className="mp-title-icon">
              <Tag />
            </span>
            Pricing Plans
          </h1>
          <p className="mp-subtitle">
            These plans are shown live on the landing page's pricing section
          </p>
        </div>
        <button type="button" onClick={openCreateModal} className="mp-btn mp-btn-primary">
          <Plus />
          Add Plan
        </button>
      </div>

      <section className="mp-card">
        <div className="mp-card-header">
          <h2 className="mp-card-title">
            <Tag /> Plans
            <span className="mp-count">{plans.length}</span>
          </h2>
          {plans.length > 0 && (
            <span className="mp-card-note">
              {activeCount} visible · {plans.length - activeCount} hidden
            </span>
          )}
        </div>

        {loading ? (
          <div className="mp-state">
            <RefreshCw className="mp-spin" />
            Loading plans...
          </div>
        ) : error ? (
          <div className="mp-alert">
            <span>{error}</span>
            <button type="button" className="mp-btn mp-btn-sm" onClick={loadPlans}>
              Retry
            </button>
          </div>
        ) : plans.length === 0 ? (
          <div className="mp-state">
            <Tag />
            <strong>No pricing plans yet</strong>
            <span>Add one to show it on the landing page.</span>
          </div>
        ) : (
          <div className="mp-table-wrap">
            <table className="mp-table">
              <thead>
                <tr>
                  <th className="mp-col-order">Order</th>
                  <th>Plan</th>
                  <th>Price</th>
                  <th>Badge</th>
                  <th>Features</th>
                  <th>Status</th>
                  <th className="mp-col-actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                {plans.map((plan, index) => (
                  <tr key={plan.id}>
                    <td data-label="Order" className="mp-col-order">
                      <div className="mp-reorder">
                        <button
                          type="button"
                          disabled={index === 0 || reordering}
                          onClick={() => movePlan(index, -1)}
                          aria-label={`Move ${plan.name} up`}
                          title="Move up"
                        >
                          <ArrowUp />
                        </button>
                        <span className="mp-order-num">{index + 1}</span>
                        <button
                          type="button"
                          disabled={index === plans.length - 1 || reordering}
                          onClick={() => movePlan(index, 1)}
                          aria-label={`Move ${plan.name} down`}
                          title="Move down"
                        >
                          <ArrowDown />
                        </button>
                      </div>
                    </td>
                    <td data-label="Plan">
                      <div className="mp-plan">
                        <div className="mp-plan-name">{plan.name}</div>
                        {plan.tagline && <div className="mp-plan-tagline">{plan.tagline}</div>}
                      </div>
                    </td>
                    <td data-label="Price">
                      <div className="mp-price-wrap">
                        <span className="mp-price">{plan.price}</span>
                        {plan.period && <span className="mp-period">{plan.period}</span>}
                      </div>
                    </td>
                    <td data-label="Badge">
                      {plan.badge ? (
                        <span className="mp-badge">{plan.badge}</span>
                      ) : (
                        <span className="mp-badge-empty">&mdash;</span>
                      )}
                    </td>
                    <td data-label="Features">
                      <span
                        className="mp-features"
                        title={plan.features.length ? plan.features.join('\n') : 'No features'}
                      >
                        {plan.features.length} feature{plan.features.length === 1 ? '' : 's'}
                      </span>
                    </td>
                    <td data-label="Status">
                      <button
                        type="button"
                        onClick={() => toggleActive(plan)}
                        className={`mp-status ${plan.is_active ? 'is-active' : 'is-hidden'}`}
                        title={plan.is_active ? 'Click to hide from the landing page' : 'Click to show on the landing page'}
                      >
                        <span className="mp-status-dot" />
                        {plan.is_active ? 'Active' : 'Hidden'}
                      </button>
                    </td>
                    <td data-label="Actions" className="mp-col-actions">
                      <div className="mp-actions">
                        <button
                          type="button"
                          onClick={() => openEditModal(plan)}
                          className="mp-icon-btn edit"
                          title="Edit plan"
                          aria-label={`Edit ${plan.name}`}
                        >
                          <Pencil />
                        </button>
                        <button
                          type="button"
                          onClick={() => setDeleteTarget(plan)}
                          className="mp-icon-btn delete"
                          title="Delete plan"
                          aria-label={`Delete ${plan.name}`}
                        >
                          <Trash2 />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Create / Edit modal */}
      {isModalOpen && (
        <div className="mp-overlay" onClick={closeModal}>
          <div
            className="mp-modal"
            role="dialog"
            aria-modal="true"
            aria-label={editingId ? 'Edit pricing plan' : 'Add pricing plan'}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mp-modal-header">
              <h3 className="mp-modal-title">{editingId ? 'Edit Pricing Plan' : 'Add Pricing Plan'}</h3>
              <button type="button" onClick={closeModal} className="mp-modal-close" aria-label="Close">
                <X />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="mp-form">
              <div className="mp-form-body">
                <div className="mp-field">
                  <label>
                    Plan Name<span className="mp-required">*</span>
                  </label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="e.g. Monthly Plan"
                    className={formErrors.name ? 'is-error' : ''}
                  />
                  {formErrors.name && <p className="mp-field-error">{formErrors.name}</p>}
                </div>

                <div className="mp-field">
                  <label>Tagline</label>
                  <input
                    type="text"
                    value={form.tagline || ''}
                    onChange={(e) => setForm({ ...form, tagline: e.target.value })}
                    placeholder="e.g. For sellers running auctions regularly"
                  />
                </div>

                <div className="mp-field-row">
                  <div className="mp-field">
                    <label>
                      Price<span className="mp-required">*</span>
                    </label>
                    <input
                      type="text"
                      value={form.price}
                      onChange={(e) => setForm({ ...form, price: e.target.value })}
                      placeholder="e.g. ₹2,499"
                      className={formErrors.price ? 'is-error' : ''}
                    />
                    {formErrors.price && <p className="mp-field-error">{formErrors.price}</p>}
                  </div>
                  <div className="mp-field">
                    <label>Billing Period</label>
                    <input
                      type="text"
                      value={form.period || ''}
                      onChange={(e) => setForm({ ...form, period: e.target.value })}
                      placeholder="e.g. / month"
                    />
                  </div>
                </div>

                <div className="mp-field-row">
                  <div className="mp-field">
                    <label>Badge</label>
                    <input
                      type="text"
                      value={form.badge || ''}
                      onChange={(e) => setForm({ ...form, badge: e.target.value })}
                      placeholder="e.g. Most Popular"
                    />
                  </div>
                  <div className="mp-field">
                    <label>Button Text</label>
                    <input
                      type="text"
                      value={form.cta_text || ''}
                      onChange={(e) => setForm({ ...form, cta_text: e.target.value })}
                      placeholder="e.g. Start Monthly Plan"
                    />
                  </div>
                </div>

                <div className="mp-field">
                  <label>Features (one per line)</label>
                  <textarea
                    rows={5}
                    value={featuresText}
                    onChange={(e) => setFeaturesText(e.target.value)}
                    placeholder={'Unlimited auctions every month\nPriority email & phone support'}
                  />
                </div>

                <label className="mp-checkbox">
                  <input
                    type="checkbox"
                    checked={!!form.is_active}
                    onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                  />
                  Visible on the landing page
                </label>
              </div>

              <div className="mp-modal-actions">
                <button type="button" onClick={closeModal} disabled={saving} className="mp-btn">
                  Cancel
                </button>
                <button type="submit" disabled={saving} className="mp-btn mp-btn-primary">
                  {saving ? (
                    <>
                      <RefreshCw className="mp-spin" />
                      Saving
                    </>
                  ) : (
                    <>
                      <Save />
                      {editingId ? 'Save Changes' : 'Create Plan'}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete confirmation */}
      {deleteTarget && (
        <div className="mp-overlay" onClick={() => !deleting && setDeleteTarget(null)}>
          <div
            className="mp-modal mp-modal-sm"
            role="alertdialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mp-confirm-body">
              <div className="mp-confirm-icon">
                <AlertTriangle />
              </div>
              <div>
                <h3 className="mp-modal-title">Delete pricing plan?</h3>
                <p className="mp-confirm-text">
                  "{deleteTarget.name}" will be permanently removed and will stop showing on the
                  landing page.
                </p>
              </div>
            </div>
            <div className="mp-modal-actions">
              <button
                type="button"
                onClick={() => setDeleteTarget(null)}
                disabled={deleting}
                className="mp-btn"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmDelete}
                disabled={deleting}
                className="mp-btn mp-btn-danger"
              >
                {deleting ? <RefreshCw className="mp-spin" /> : <Trash2 />}
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManagePricing;
